<template>
  <li class="tree-node-item" :style="{ paddingLeft: level > 0 ? '38px' : '0' }">
    <div
      class="dropdown-item tree-node-row"
      :data-testid="`lead-tree-node-${encodeURIComponent(String(resolvedValue))}`"
      :class="{
        'is-highlighted': isHighlighted,
        'is-selected': isSelected,
        'has-leading': hasChildren || shouldShowAvatar,
      }"
      :data-node-id="resolvedValue"
      @mousedown.prevent="handleRowSelect"
      @mouseenter="emit('highlight-node', node)">
      <!-- For nodes with children, a chevron occupies the leading slot instead of
           the avatar — matches the pjas-frontend TreeSelect convention. -->
      <button
        v-if="hasChildren"
        type="button"
        class="tree-toggle"
        tabindex="-1"
        @mousedown.prevent.stop
        @click.stop="emit('toggle-expand', resolvedValue)"
        :aria-label="isExpanded ? 'Collapse' : 'Expand'">
        <IconChevronDown v-if="isExpanded" size="18" class="tree-toggle-icon" />
        <IconChevronRight v-else size="18" class="tree-toggle-icon" />
      </button>
      <div v-else-if="shouldShowAvatar" class="entity-avatar" :class="avatarBgClass">
        <img v-if="node.imageUrl" :src="node.imageUrl" :alt="resolvedLabel" />
        <span v-else class="initials">{{ initialsText }}</span>
      </div>

      <span class="item-name">
        <template v-for="(part, idx) in highlightedParts" :key="idx">
          <span v-if="part.match" class="tree-node-highlight">{{ part.text }}</span>
          <template v-else>{{ part.text }}</template>
        </template>
      </span>
    </div>

    <ul v-if="hasChildren && isExpanded" class="tree-children-list">
      <LeadTreeNode
        v-for="child in node.children"
        :key="resolveValue(child)"
        :node="child"
        :level="level + 1"
        :selected-node="selectedNode"
        :highlighted-node-id="highlightedNodeId"
        :expanded-nodes="expandedNodes"
        :search-value="searchValue"
        :display-initials="displayInitials"
        :get-option-label="getOptionLabel"
        :get-option-value="getOptionValue"
        @select-node="(n: OptionItem) => emit('select-node', n)"
        @toggle-expand="(id: string | number) => emit('toggle-expand', id)"
        @highlight-node="(n: OptionItem) => emit('highlight-node', n)" />
    </ul>
  </li>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-vue';
import { getInitials } from '@/helpers/helpers';

interface OptionItem {
  id?: string | number;
  name?: string;
  imageUrl?: string;
  initials?: string;
  colorVariant?: string;
  children?: OptionItem[];
  [key: string]: any;
}

const props = withDefaults(
  defineProps<{
    node: OptionItem;
    level?: number;
    selectedNode?: OptionItem | null;
    highlightedNodeId?: string | number | null;
    expandedNodes: (string | number)[];
    searchValue?: string;
    displayInitials?: boolean;
    getOptionLabel?: string | ((option: OptionItem) => string);
    getOptionValue?: string | ((option: OptionItem) => any);
  }>(),
  {
    level: 0,
    selectedNode: null,
    highlightedNodeId: null,
    searchValue: '',
    displayInitials: false,
  }
);

const emit = defineEmits<{
  (e: 'select-node', node: OptionItem): void;
  (e: 'toggle-expand', id: string | number): void;
  (e: 'highlight-node', node: OptionItem): void;
}>();

const resolveLabel = (option: OptionItem): string => {
  if (!option) return '';
  if (typeof props.getOptionLabel === 'function') return props.getOptionLabel(option);
  const key = (props.getOptionLabel as string) || 'name';
  return option[key] ?? '';
};

const resolveValue = (option: OptionItem): any => {
  if (!option) return null;
  if (typeof props.getOptionValue === 'function') return props.getOptionValue(option);
  const key = (props.getOptionValue as string) || 'id';
  return option[key];
};

const resolvedLabel = computed(() => resolveLabel(props.node));
const resolvedValue = computed(() => resolveValue(props.node));

const hasChildren = computed(() => Array.isArray(props.node.children) && props.node.children.length > 0);

// Expansion is either explicitly flagged on the node (used by the filter to
// force-open ancestors of a match) or driven by the user's expandedNodes list.
const isExpanded = computed(() => {
  if (props.node.isExpanded === true) return true;
  return props.expandedNodes.includes(resolvedValue.value);
});

const isSelected = computed(() => {
  if (!props.selectedNode) return false;
  return resolveValue(props.selectedNode) === resolvedValue.value;
});

const isHighlighted = computed(() => {
  if (props.highlightedNodeId === null || props.highlightedNodeId === undefined) return false;
  return props.highlightedNodeId === resolvedValue.value;
});

const initialsText = computed(() => getInitials(props.node, resolveLabel, props.displayInitials));
const shouldShowAvatar = computed(() => !!(props.node.imageUrl || initialsText.value));

const avatarBgClass = computed(() => {
  const variant = props.node.colorVariant || 'gray';
  return `bg-variant-${variant}`;
});

// Split the label into alternating matched/unmatched segments for highlighting.
// Rendered with spans rather than v-html so names containing `<` / `>` can't
// be interpreted as markup.
const highlightedParts = computed<{ text: string; match: boolean }[]>(() => {
  const label = resolvedLabel.value;
  const q = props.searchValue;
  if (!q) return [{ text: label, match: false }];

  const parts: { text: string; match: boolean }[] = [];
  const lowerLabel = label.toLowerCase();
  const lowerQ = q.toLowerCase();
  let cursor = 0;
  while (cursor < label.length) {
    const idx = lowerLabel.indexOf(lowerQ, cursor);
    if (idx === -1) {
      parts.push({ text: label.slice(cursor), match: false });
      break;
    }
    if (idx > cursor) parts.push({ text: label.slice(cursor, idx), match: false });
    parts.push({ text: label.slice(idx, idx + q.length), match: true });
    cursor = idx + q.length;
  }
  return parts;
});

const handleRowSelect = () => {
  // clicking a node — whether it has children or not — selects it.
  // expand/collapse is handled by the dedicated chevron button.
  emit('select-node', props.node);
};
</script>

<style scoped>
.tree-node-item {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tree-children-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  height: 42px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.dropdown-item.has-leading {
  padding-left: 4px;
}

.dropdown-item.is-highlighted {
  background-color: #f3f4f6;
}

.dropdown-item.is-selected {
  background-color: #eff6ff;
}

/* Chevron and avatar share the same leading slot — never both shown. */
.tree-toggle {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  margin-right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 5px;
  color: #6b7280;
}

.tree-toggle:hover {
  background-color: #e5e7eb;
  color: #111827;
}

.tree-toggle-icon {
  display: block;
}

.entity-avatar {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  overflow: hidden;
  font-weight: 400;
  font-size: 13px;
  color: #000;
}

.entity-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.entity-avatar .initials {
  color: #000;
  font-weight: 400;
  font-size: 13px;
}

.bg-variant-gray {
  background-color: #e5e7eb;
}

.item-name {
  flex-grow: 1;
  font-size: 16px;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-node-highlight {
  background-color: #dbeafe;
  padding: 0 2px;
  border-radius: 3px;
}
</style>
